import { cleanUrl, displayHost } from '../utils/url';

/** The API sends `language` as a list of { id, name } (docs-api-data.ts
 *  says a string); both are read, as names. */
function languageNames(value: unknown): string[] {
  const list = Array.isArray(value) ? value : value == null ? [] : [value];
  return list
    .map(l => (typeof l === 'string' ? l : (l as { name?: unknown })?.name))
    .filter((name): name is string => typeof name === 'string' && name.trim().length > 0);
}

/** Full text as sources send it often opens with blank lines and tab
 *  indents (left over from their HTML): trim the ends, drop trailing
 *  spaces/tabs per line, and keep at most one empty line between blocks,
 *  so the shown text starts with text. The words themselves are untouched. */
function tidyText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value
    .replace(/\r\n?/g, '\n')
    .split('\n').map(line => line.trim()).join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text || null;
}

/** One /search article, per the documented `search_hit` schema
 *  (docs-api-data.ts). `url` is always there; every other field only when
 *  its include_* toggle was on (and the article carries it), otherwise null
 *  here. */
export class SearchHit {
  url!: string;
  /** include_title */
  title: string | null = null;
  /** include_domain_name */
  domainName: string | null = null;
  /** include_publication_date */
  publicationDate: string | null = null;
  /** include_indexation_date. When Cikisi saw it, sometimes much later. */
  indexationDate: string | null = null;
  /** include_relevance_score. Elasticsearch's own score, not 0–1. */
  relevanceScore: number | null = null;
  /** include_favicon */
  favicon: string | null = null;
  /** include_description */
  description: string | null = null;
  /** include_author */
  author: string | null = null;
  /** include_language. Language names, e.g. ["English"]. */
  languages: string[] = [];
  /** include_content. Full text, where the source's licence allows it. */
  content: string | null = null;
  /** include_image_url */
  imageUrl: string | null = null;

  constructor(data: any = {}) {
    this.url = cleanUrl(data.url) ?? '';
    this.title = data.title || null;
    this.domainName = data.domain_name || null;
    this.publicationDate = data.publication_date ?? null;
    this.indexationDate = data.indexation_date ?? null;
    this.relevanceScore = typeof data.relevance_score === 'number' ? data.relevance_score : null;
    this.favicon = cleanUrl(data.favicon);
    this.description = data.description || null;
    this.author = data.author || null;
    this.languages = languageNames(data.language);
    this.content = tidyText(data.content);
    this.imageUrl = cleanUrl(data.image_url);
  }

  /** The domain to show, without "www.". */
  get host(): string {
    return displayHost(this.domainName, this.url);
  }
}
